import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, KeyRound, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../../../services/apiClient';
import { getWhatsAppLink } from '../../../utils/whatsapp';

// Modal admin: genera una invitación de acceso para un alumno/profe y muestra el enlace
// (copiable) + usuario + botón de WhatsApp para enviarlo a mano. NO envía nada solo.
type InviteResp = {
  token: string;
  path: string;
  expires_at: string;
  account: {
    account_id: number;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    username: string | null;
    phone_prefix: string | null;
    phone_number: string | null;
    has_password: boolean;
  };
};

export default function AccessModal({ accountId, onClose }: { accountId: number; onClose: () => void }) {
  const [data, setData] = useState<InviteResp | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    // Body {} obligatorio: el proxy LiteSpeed rechaza POST con Content-Type JSON y cuerpo vacío.
    api.post<InviteResp>(`/auth/invite/${accountId}`, {})
      .then((r) => { if (alive) setData(r); })
      .catch((e: ApiError) => { if (alive) setError(e?.message || 'No se pudo generar el acceso.'); });
    return () => { alive = false; };
  }, [accountId]);

  const link = data ? `${window.location.origin}${data.path}` : '';
  const nombre = data?.account.firstname || '';
  const usuario = data?.account.username || data?.account.email || '';
  const waMsg = data
    ? `¡Hola ${nombre}! 👋 Ya puedes entrar a tu portal de Let's Move Dance & Fitness.\n\n` +
      `1️⃣ Abre este enlace para crear tu contraseña:\n${link}\n\n` +
      `2️⃣ Tu usuario para entrar es: ${usuario}\n\n` +
      `El enlace vence en 7 días. ¡Nos vemos en clase! 💃`
    : '';
  const waLink = data ? getWhatsAppLink(data.account.phone_prefix, data.account.phone_number, waMsg) : '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard no disponible */ }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            <KeyRound size={18} className="text-accent-ink" /> Generar acceso
          </h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>

        {error ? (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        ) : !data ? (
          <p className="py-6 text-center text-sm text-content-3">Generando enlace…</p>
        ) : (
          <div className="space-y-4">
            {data.account.has_password && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Esta persona <strong>ya tenía acceso</strong>. Este enlace <strong>restablecerá</strong> su contraseña.
              </div>
            )}

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-3">Usuario para entrar</div>
              <div className="rounded-lg bg-surface-2 px-3 py-2 text-sm font-semibold text-content">{usuario || '—'}</div>
            </div>

            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-3">Enlace para crear contraseña</div>
              <div className="flex items-stretch gap-2">
                <input readOnly value={link} onFocus={(e) => e.currentTarget.select()}
                  className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-xs text-content-2 outline-none" />
                <button onClick={copy}
                  className="flex shrink-0 items-center gap-1 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                  {copied ? <><Check size={15} className="text-emerald-600" /> Copiado</> : <><Copy size={15} /> Copiar</>}
                </button>
              </div>
              <p className="mt-1 text-xs text-content-3">Vence en 7 días · de un solo uso.</p>
            </div>

            {waLink ? (
              <a href={waLink} target="_blank" rel="noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] py-2.5 text-sm font-semibold text-white transition hover:brightness-95">
                Enviar por WhatsApp
              </a>
            ) : (
              <div className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-center text-xs text-content-3">
                Sin teléfono registrado — copia el enlace y envíalo por el medio que prefieras.
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
